import type {
  ChatMessage,
  ConversationalFormOption,
  MenuContent,
} from '@karehero/models';

import { message } from './chatMessages';

/** A care profile question the server asked, by the menu message asking it. */
export interface ServerQuestion {
  messageId: string;
  content: MenuContent & { questionId: string };
}

const asQuestion = (m: ChatMessage): ServerQuestion | undefined =>
  m.from === 'system' && m.content.kind === 'menu' && m.content.questionId
    ? {
        messageId: m.id,
        content: { ...m.content, questionId: m.content.questionId },
      }
    : undefined;

/** The question still being asked: the last the server asked, with nothing said to it since. */
export const openQuestion = (
  messages: readonly ChatMessage[],
): ServerQuestion | undefined => {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const m = messages[i];
    if (m.from === 'user') return undefined;
    const question = asQuestion(m);
    if (question) return question;
  }
  return undefined;
};

/** The first answer to a question after it was asked: the message, and the options picked. */
const answerAfter = (
  messages: readonly ChatMessage[],
  index: number,
  questionId: string,
): { messageId: string; optionIds: string[] } | undefined => {
  for (const m of messages.slice(index + 1)) {
    // Asked again before it was answered: the answer is the later asking's.
    if (asQuestion(m)?.content.questionId === questionId) return undefined;
    if (m.content.kind !== 'answers' || m.status === 'error') continue;
    const answer = m.content.answers.find((a) => a.questionId === questionId);
    if (answer) return { messageId: m.id, optionIds: answer.optionIds ?? [] };
  }
  return undefined;
};

/** Answers read from what the member said, which their own message already shows. */
const isReadFromSaid = (m: ChatMessage) =>
  m.content.kind === 'answers' && !!m.content.readFrom;

/**
 * The messages as drawn: a question answered by picking shows the pick, in
 * place of the answer's own message, and one answered some other way folds
 * its options away. Several picks are made in rows of their own, so a
 * multi-select question always folds, and its answer shows as said. Answers
 * read from what the member said show only as what they said.
 */
export const shownQuestions = (
  messages: readonly ChatMessage[],
): ChatMessage[] => {
  const openId = openQuestion(messages)?.messageId;
  const hiddenIds = new Set<string>();
  const shown = messages.map((m, i) => {
    const question = asQuestion(m);
    if (!question) return m;
    const { content } = question;
    const answer = answerAfter(messages, i, content.questionId);
    const pickedId = content.isMultiSelect ? undefined : answer?.optionIds[0];
    if (pickedId && answer) hiddenIds.add(answer.messageId);
    return {
      ...m,
      content: {
        ...content,
        pickedId,
        isClosed: !pickedId && (m.id !== openId || !!content.isMultiSelect),
      },
    };
  });
  return shown.filter((m) => !hiddenIds.has(m.id) && !isReadFromSaid(m));
};

/** Answers a question with the options picked, as a message of the member's. */
export const answersMessage = (
  question: ServerQuestion,
  options: ConversationalFormOption[],
): ChatMessage =>
  message('user', {
    kind: 'answers',
    text: options.map((o) => o.label).join(', '),
    answers: [
      {
        questionId: question.content.questionId,
        optionIds: options.map((o) => o.id),
      },
    ],
  });
